<script setup lang="ts">
import { useProductNoun } from "@/composition/useProductNoun";
import {
  getDateTimePartsInTimezone,
  getScheduleErrors,
  getTimezoneOptions,
  isHourDisabledFor,
  isMinuteDisabledFor,
  type ScheduleBoundary,
  type ScheduleFieldError,
} from "@/util/formSchedule";
import {
  ClockIcon,
  Globe02Icon,
  InfinityIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIDatepicker,
  UISelect,
  UITimePicker,
} from "@gohighlevel/ghl-ui";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

dayjs.extend(customParseFormat);

const { t } = useI18n();
const { productParams } = useProductNoun();

const props = defineProps<{
  open: ScheduleBoundary;
  close: ScheduleBoundary;
  timezone: string;
}>();

const emit = defineEmits<{
  (e: "update:open", value: ScheduleBoundary): void;
  (e: "update:close", value: ScheduleBoundary): void;
  (e: "update:timezone", value: string): void;
}>();

const timezoneOptions = getTimezoneOptions();

const isOpenDated = computed(() => props.open.mode === "date");
const isCloseDated = computed(() => props.close.mode === "date");

const dateToMillis = (date: string): number | undefined =>
  date ? dayjs(date, "YYYY-MM-DD").valueOf() : undefined;

const timeToMillis = (time: string): number | undefined =>
  time ? dayjs(time, "HH:mm").valueOf() : undefined;

// ---- Validation ----
// Past boundaries aren't errors (they mean the form has opened/closed); the
// pickers prevent choosing past values, so only close-vs-open can conflict.
const errors = computed(() => getScheduleErrors(props.open, props.close));

const errorText = (code: ScheduleFieldError | null): string =>
  code === "closeBeforeOpen" ? t("form.scheduleVisibility.closeAfterOpenError") : "";

const closeErrorMsg = computed(() => errorText(errors.value.close));

// Disable calendar days before today (open) / before max(today, open date) (close).
// The schedule's date strings are wall-clock values in the SELECTED timezone,
// so "today" is derived there — not in the builder user's browser zone. The
// picker's day timestamps are browser-local, so compare as date strings.
const todayInScheduleTz = (): string => getDateTimePartsInTimezone(props.timezone).date;

const disableOpenDate = (ts: number): boolean =>
  dayjs(ts).format("YYYY-MM-DD") < todayInScheduleTz();

const disableCloseDate = (ts: number): boolean => {
  const day = dayjs(ts).format("YYYY-MM-DD");
  let min = todayInScheduleTz();
  if (props.open.mode === "date" && props.open.date && props.open.date > min) {
    min = props.open.date;
  }
  return day < min;
};

// Disable past times when the selected day is today (in the selected timezone);
// the close picker is additionally floored by the open time when both land on
// the same day. Plain functions (not computeds) so "now" is fresh each time a
// panel renders.
const disableOpenHour = (hour: number): boolean =>
  isHourDisabledFor(props.open.date, null, hour, new Date(), props.timezone);
const disableOpenMinute = (minute: number, hour: number | null): boolean =>
  isMinuteDisabledFor(props.open.date, null, minute, hour, new Date(), props.timezone);
const disableCloseHour = (hour: number): boolean =>
  isHourDisabledFor(props.close.date, props.open, hour, new Date(), props.timezone);
const disableCloseMinute = (minute: number, hour: number | null): boolean =>
  isMinuteDisabledFor(props.close.date, props.open, minute, hour, new Date(), props.timezone);

// First switch to "Pick a date" starts from now IN THE SELECTED TIMEZONE (next
// minute, so the value isn't instantly "in the past" for the saved schedule);
// a previously chosen date/time is kept.
const withDefaultDateTime = (boundary: ScheduleBoundary): ScheduleBoundary => {
  if (boundary.mode !== "date" || boundary.date || boundary.time) {
    return boundary;
  }
  const nextMinute = new Date(Date.now() + 60_000);
  const parts = getDateTimePartsInTimezone(props.timezone, nextMinute);
  return { ...boundary, date: parts.date, time: parts.time };
};

const setOpenMode = (mode: ScheduleBoundary["mode"]) => {
  emit("update:open", withDefaultDateTime({ ...props.open, mode }));
};

const setCloseMode = (mode: ScheduleBoundary["mode"]) => {
  emit("update:close", withDefaultDateTime({ ...props.close, mode }));
};

const setOpenDate = (value: number | null) => {
  emit("update:open", {
    ...props.open,
    date: value ? dayjs(value).format("YYYY-MM-DD") : "",
  });
};

const setOpenTime = (value: number | null) => {
  emit("update:open", {
    ...props.open,
    time: value ? dayjs(value).format("HH:mm") : "",
  });
};

const setCloseDate = (value: number | null) => {
  emit("update:close", {
    ...props.close,
    date: value ? dayjs(value).format("YYYY-MM-DD") : "",
  });
};

const setCloseTime = (value: number | null) => {
  emit("update:close", {
    ...props.close,
    time: value ? dayjs(value).format("HH:mm") : "",
  });
};
</script>

<template>
  <div class="window-fields">
    <!-- Form opens -->
    <div class="window-column">
      <p class="window-label">{{ t("form.scheduleVisibility.formOpens", productParams) }}</p>
      <div class="seg" role="group">
        <button
          id="schedule-open-mode-anytime"
          type="button"
          class="seg-btn"
          :class="{ 'seg-btn--active': open.mode === 'anytime' }"
          @click="setOpenMode('anytime')"
        >
          {{ t("form.scheduleVisibility.rightAway") }}
        </button>
        <button
          id="schedule-open-mode-date"
          type="button"
          class="seg-btn"
          :class="{ 'seg-btn--active': open.mode === 'date' }"
          @click="setOpenMode('date')"
        >
          {{ t("form.scheduleVisibility.pickADate") }}
        </button>
      </div>

      <div v-if="isOpenDated" class="window-inputs">
        <UIDatepicker
          id="schedule-open-date"
          class="flex-1"
          :value="dateToMillis(open.date)"
          format="MM/dd/yyyy"
          :actions="[]"
          :is-date-disabled="disableOpenDate"
          @update:value="setOpenDate"
        />
        <UITimePicker
          id="schedule-open-time"
          class="flex-1"
          :value="timeToMillis(open.time)"
          format="h:mm a"
          :is-hour-disabled="disableOpenHour"
          :is-minute-disabled="disableOpenMinute"
          @update:value="setOpenTime"
        />
      </div>
      <div v-else class="window-placeholder">
        <ClockIcon class="h-4 w-4 text-gray-400" />
        <span>{{ t("form.scheduleVisibility.opensPlaceholder") }}</span>
      </div>
    </div>

    <!-- Form closes -->
    <div class="window-column">
      <p class="window-label">{{ t("form.scheduleVisibility.formCloses", productParams) }}</p>
      <div class="seg" role="group">
        <button
          id="schedule-close-mode-never"
          type="button"
          class="seg-btn"
          :class="{ 'seg-btn--active': close.mode === 'never' }"
          @click="setCloseMode('never')"
        >
          {{ t("form.scheduleVisibility.never") }}
        </button>
        <button
          id="schedule-close-mode-date"
          type="button"
          class="seg-btn"
          :class="{ 'seg-btn--active': close.mode === 'date' }"
          @click="setCloseMode('date')"
        >
          {{ t("form.scheduleVisibility.pickADate") }}
        </button>
      </div>

      <div v-if="isCloseDated" class="window-inputs">
        <UIDatepicker
          id="schedule-close-date"
          class="flex-1"
          :value="dateToMillis(close.date)"
          format="MM/dd/yyyy"
          :is-date-disabled="disableCloseDate"
          @update:value="setCloseDate"
        />
        <UITimePicker
          id="schedule-close-time"
          class="flex-1"
          :value="timeToMillis(close.time)"
          format="h:mm a"
          :is-hour-disabled="disableCloseHour"
          :is-minute-disabled="disableCloseMinute"
          @update:value="setCloseTime"
        />
      </div>
      <div v-else class="window-placeholder">
        <InfinityIcon class="h-4 w-4 text-gray-400" />
        <span>{{ t("form.scheduleVisibility.closesPlaceholder") }}</span>
      </div>
      <p v-if="closeErrorMsg" class="window-error">{{ closeErrorMsg }}</p>
    </div>

    <!-- Time zone spans both columns -->
    <div class="window-timezone">
      <p class="window-label window-label--timezone">
        {{ t("form.scheduleVisibility.timezone") }}
      </p>
      <!-- Globe sits inside the select trigger, before the value (matches design). -->
      <div class="tz-select">
        <Globe02Icon class="tz-select__icon" />
        <UISelect
          id="schedule-timezone"
          :value="timezone"
          :options="timezoneOptions"
          filterable
          :placeholder="t('form.scheduleVisibility.timezonePlaceholder')"
          @update:value="emit('update:timezone', $event)"
        />
      </div>
      <p class="window-help">{{ t("form.scheduleVisibility.timezoneHelp") }}</p>
    </div>
  </div>
</template>

<style scoped>
.window-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.window-column {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.seg {
  display: inline-flex;
  align-self: flex-start;
  gap: 2px;
  padding: 3px;
  border-radius: 8px;
  background: #f2f4f7;
}

.seg-btn {
  padding: 6px 14px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #475467;
  cursor: pointer;
  outline: none;
}

.seg-btn:focus-visible {
  box-shadow: 0 0 0 2px #155eef;
}

.seg-btn--active {
  background: #ffffff;
  color: #155eef;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.06);
}

.window-timezone {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Divider separating the open/close selectors from the time zone (matches design). */
  padding-top: 20px;
  border-top: 1px solid #eaecf0;
}

/* Reset the default paragraph margins on this child component's text — the
   Settings container's scoped `p { margin: 0 }` reset doesn't reach here, so
   labels/help otherwise carry stray vertical space. Spacing comes from the
   flex `gap` on the columns. */
.window-fields p {
  margin: 0;
}

.window-label {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

/* The time zone label is lighter than the column labels (matches design). */
.window-label--timezone {
  font-weight: 500;
  color: #344054;
}

.tz-select {
  position: relative;
}

.tz-select__icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #667085;
  pointer-events: none;
  z-index: 1;
}

/* Make room for the globe icon inside the select trigger. naive-ui sets this
   padding variable as an inline style, so !important is required to win. */
.tz-select :deep(.n-base-selection) {
  --n-padding-single-left: 38px !important;
}

.window-inputs {
  display: flex;
  gap: 8px;
}

/* naive-ui strikes the held value (inline line-through) once it drifts behind
   the disabled hours/minutes; we only want the dropdown options disabled. */
.window-inputs :deep(.n-input__input-el) {
  text-decoration: none !important;
}

.window-placeholder {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  color: #667085;
}

.window-help {
  font-size: 12px;
  color: #667085;
}

.window-error {
  font-size: 12px;
  color: #d92d20;
}
</style>
